import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CSS_VARIABLES from './styles/variables';
import { Options } from './helpers/datasetToOptions';
import './styles/index.css';
import I18nProvider from './context/I18nProvider';
import OptionsProvider from './context/OptionsProvider';
import { InferredBookingFlowOptions, InferredCalendarOptions, InferredMyEnvironmentOptions } from './types/options';
import MyEnvironment from './containers/MyEnvironment';
import Calendar from './containers/Calendar';
import BookingFlow from './containers/BookingFlow';

// Define allowed component names
export const allowedComponentNames = ['calendar', 'booking-flow', 'my-environment'] as const;

export type AllowedComponentName = typeof allowedComponentNames[number];

type RendererProps = {
  componentName: AllowedComponentName;
  node: Element;
  options: Options;
};

const queryClient = new QueryClient();

function render(component: React.ReactNode, mountNode: Element, options: Options) {
  ReactDOM.createRoot(mountNode).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <I18nProvider initialLocale={options.locale}>
          <OptionsProvider options={options}>
            {/* @ts-expect-error CSS_VARIABLES is a valid React style object */}
            <div style={CSS_VARIABLES} className="scape-sdk">
              {component}
            </div>
          </OptionsProvider>
        </I18nProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

export default function renderer({
  componentName,
  node,
  options,
}: RendererProps) {
  if (componentName === 'calendar') {
    const calendarOptions = options as InferredCalendarOptions;
    return render(<Calendar />, node, calendarOptions);
  }

  if (componentName === 'booking-flow') {
    const bookingFlowOptions = options as InferredBookingFlowOptions;
    return render(<BookingFlow />, node, bookingFlowOptions);
  }

  if (componentName === 'my-environment') {
    const myEnvironmentOptions = options as InferredMyEnvironmentOptions;
    return render(<MyEnvironment />, node, myEnvironmentOptions);
  }

  throw new Error('Component not handled in renderer');
}
